import React, { useEffect, useState, useRef } from 'react';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal, Tabs } from '../components/ui';
import {
  Upload, Link2, X, Search, Plus, Pencil, Trash2,
  Building2, Wifi, WifiOff, RefreshCw, Scale, Zap, FilePlus, Paperclip,
} from 'lucide-react';

const idag = () => new Date().toISOString().slice(0, 10);
const forsteIaar = () => `${new Date().getFullYear()}-01-01`;

const Bank = () => {
  const [tab, setTab] = useState('transaksjoner');
  return (
    <>
      <PageHeader title="Bank"
        subtitle="Bankforbindelser, transaksjoner, matching og avstemming" />

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: 'transaksjoner', label: 'Transaksjoner' },
          { key: 'forbindelser',  label: 'Bankforbindelser' },
          { key: 'regler',        label: 'Regelmotor' },
          { key: 'avstemming',    label: 'Avstemming' },
        ]}
      />

      {tab === 'transaksjoner' && <Transaksjoner />}
      {tab === 'forbindelser'  && <Forbindelser />}
      {tab === 'regler'        && <Regler />}
      {tab === 'avstemming'    && <Avstemming />}
    </>
  );
};

// ── Transaksjoner-tab ────────────────────────────────────────────────────────
const Transaksjoner = () => {
  const [tx, setTx] = useState([]);
  const [filter, setFilter] = useState('ny');
  const [bfFilter, setBfFilter] = useState('');     // '' = alle, ellers id
  const [bfImport, setBfImport] = useState('');     // bankforbindelse for CSV-import
  const [bfs, setBfs] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importFil, setImportFil] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aktiv, setAktiv] = useState(null);
  const [forslag, setForslag] = useState([]);
  const [bilagForm, setBilagForm] = useState(null); // null = skjult, objekt = synlig
  const [innboksEdit, setInnboksEdit] = useState(null);
  const [innboksBildeUrl, setInnboksBildeUrl] = useState('');
  const bilagFil = useRef(null);
  const fil = useRef();

  const bfNavn = (id) => {
    const b = bfs.find((x) => x.id === id);
    return b ? (b.navn || b.bank_navn || `#${b.id}`) : '';
  };

  const last = async () => {
    const params = filter === 'alle' ? {} : { status: filter };
    if (bfFilter) params.bankforbindelse_id = bfFilter;
    const r = await api.get('/bank/transaksjoner', { params });
    setTx(r.data);
  };
  useEffect(() => { last(); /* eslint-disable-next-line */ }, [filter, bfFilter]);
  useEffect(() => {
    api.get('/bankforbindelse').then((r) => {
      const data = r.data || [];
      setBfs(data);
      // Default import-konto til første tilgjengelige slik at man ikke importerer uten kobling ved uhell
      if (data.length > 0) setBfImport(String(data[0].id));
    }).catch(() => setBfs([]));
  }, []);

  const importer = async () => {
    if (!importFil) { alert('Velg en CSV-fil.'); return; }
    setBusy(true);
    const fd = new FormData(); fd.append('fil', importFil);
    if (bfImport) fd.append('bankforbindelse_id', bfImport);
    try {
      const r = await api.post('/bank/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(`Importert: ${r.data.nye} nye, ${r.data.feilet} feilet`);
      setImportOpen(false); setImportFil(null);
      if (fil.current) fil.current.value = '';
      last();
    } catch (err) { alert(err.response?.data?.error || 'Import feilet.'); }
    setBusy(false);
  };

  const autoMatch = async () => {
    if (!window.confirm('Kjør automatisk match for alle nye transaksjoner med høy treffsannsynlighet?')) return;
    setBusy(true);
    try {
      const r = await api.post('/bank/auto-match', { terskel: 70, margin: 30 });
      alert(`${r.data.matchet} transaksjoner ble auto-matchet.`);
      last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
    setBusy(false);
  };

  const aapneMatch = async (t) => {
    setAktiv(t);
    setBilagForm(null);
    try {
      const r = await api.get(`/bank/forslag/${t.id}`);
      setForslag(r.data);
    } catch { setForslag([]); }
  };

  const startNyttBilag = (t) => {
    setBilagForm({
      hovedkonto: '',
      motkonto: '1920',
      mva_sats: t.belop < 0 ? 25 : 0,
      beskrivelse: t.tekst || '',
      type: t.belop < 0 ? 'utgift' : 'inntekt',
      dato: t.dato,
    });
    setTimeout(() => bilagFil.current && (bilagFil.current.value = ''), 0);
  };

  const opprettOgMatch = async () => {
    if (!bilagForm.hovedkonto) { alert('Hovedkonto er påkrevd.'); return; }
    const fd = new FormData();
    Object.entries(bilagForm).forEach(([k, v]) => fd.append(k, v ?? ''));
    if (bilagFil.current?.files?.[0]) fd.append('fil', bilagFil.current.files[0]);
    try {
      await api.post(`/bank/${aktiv.id}/opprett-bilag`, fd,
                     { headers: { 'Content-Type': 'multipart/form-data' } });
      setAktiv(null); setBilagForm(null); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const matchTil = async (felt, id) => {
    try {
      await api.post(`/bank/${aktiv.id}/match`, { [felt]: id });
      setAktiv(null); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const apneInnboksEdit = (innboks) => {
    let o = {};
    try { o = innboks.ocr_data ? JSON.parse(innboks.ocr_data) : {}; } catch { o = {}; }
    setBilagForm(null);
    setInnboksEdit({
      id: innboks.id,
      filnavn: innboks.filnavn || '',
      mime: innboks.mime || '',
      dato: o.dato || aktiv?.dato || '',
      beskrivelse: o.beskrivelse || o.leverandor || innboks.notat || '',
      kategori: o.kategori || '',
      sum: o.sum != null ? o.sum : Math.abs(aktiv?.belop || 0),
      mva: o.mva != null ? o.mva : '',
    });
    if (innboksBildeUrl) { URL.revokeObjectURL(innboksBildeUrl); setInnboksBildeUrl(''); }
    const tok = localStorage.getItem('token');
    fetch(`${api.defaults.baseURL}/innboks/${innboks.id}/bilde`, {
      headers: { Authorization: `Bearer ${tok}` },
    }).then((r) => r.blob()).then((b) => setInnboksBildeUrl(URL.createObjectURL(b))).catch(() => {});
  };

  const lukkInnboksEdit = () => {
    if (innboksBildeUrl) URL.revokeObjectURL(innboksBildeUrl);
    setInnboksBildeUrl(''); setInnboksEdit(null);
  };

  const bekreftInnboksMatch = async () => {
    if (!innboksEdit) return;
    try {
      const body = {
        dato: innboksEdit.dato || undefined,
        beskrivelse: innboksEdit.beskrivelse || undefined,
        kategori: innboksEdit.kategori || undefined,
        sum: innboksEdit.sum !== '' ? Number(innboksEdit.sum) : undefined,
        mva: innboksEdit.mva !== '' ? Number(innboksEdit.mva) : undefined,
      };
      const r = await api.post(`/innboks/${innboksEdit.id}/til-bilag`, body);
      const bid = r.data?.bilag_id;
      if (!bid) throw new Error('Mangler bilag_id');
      await api.post(`/bank/${aktiv.id}/match`, { bilag_id: bid });
      lukkInnboksEdit();
      setAktiv(null);
      last();
    } catch (e) { alert(e.response?.data?.error || e.message || 'Feilet.'); }
  };

  const ignorer = async (t) => { await api.post(`/bank/${t.id}/ignorer`); last(); };

  return (
    <>
      <Card style={{ marginBottom: 12, padding: 8 }}>
        <div className="flex items-center justify-between" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Tabs
            value={filter}
            onChange={setFilter}
            style={{ marginBottom: 0, padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
            items={[
              { key: 'ny',       label: 'Nye' },
              { key: 'matchet',  label: 'Matchet' },
              { key: 'ignorert', label: 'Ignorert' },
              { key: 'alle',     label: 'Alle' },
            ]}
          />
          <div className="flex" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {bfs.length > 0 && (
              <select value={bfFilter} onChange={(e) => setBfFilter(e.target.value)}
                      style={{ width: 200, height: 32, padding: '4px 8px', fontSize: 12 }}
                      title="Filtrer på bankforbindelse">
                <option value="">Alle bankforbindelser</option>
                {bfs.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.navn || b.bank_navn || `#${b.id}`}
                    {b.kontonummer ? ` — ${b.kontonummer}` : ''}
                  </option>
                ))}
              </select>
            )}
            <button className="btn-ghost" onClick={autoMatch} disabled={busy}>
              <Zap size={14} /> Auto-match
            </button>
            <button className="btn-primary" onClick={() => { setImportFil(null); setImportOpen(true); }} disabled={busy}>
              <Upload size={14} /> Importer CSV
            </button>
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        <table>
          <thead><tr><th>Dato</th><th>Tekst</th><th>Konto</th><th>KID</th><th style={{ textAlign: 'right' }}>Beløp</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {tx.length === 0 && <tr><td colSpan={7} className="fm-text-muted" style={{ textAlign: 'center', padding: 16 }}>Ingen transaksjoner.</td></tr>}
            {tx.map((t) => (
              <tr key={t.id}>
                <td>{dato(t.dato)}</td>
                <td>{t.tekst}</td>
                <td className="fm-text-muted" style={{ fontSize: 11 }}>{bfNavn(t.bankforbindelse_id) || '—'}</td>
                <td className="fm-text-muted" style={{ fontSize: 11 }}>{t.kid}</td>
                <td style={{ textAlign: 'right', color: t.belop >= 0 ? 'var(--theme-success)' : 'var(--theme-danger)' }}>
                  {kr(t.belop)}
                </td>
                <td><span className={`badge ${t.status === 'matchet' ? 'badge-success' : t.status === 'ignorert' ? 'badge-neutral' : 'badge-warning'}`}>{t.status}</span></td>
                <td>
                  {t.status === 'ny' && (
                    <div className="flex" style={{ gap: 4 }}>
                      <button className="btn-ghost" onClick={() => aapneMatch(t)}><Link2 size={12} /> Match</button>
                      <button className="btn-ghost" onClick={() => ignorer(t)}><X size={12} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={!!aktiv} onClose={() => { setAktiv(null); lukkInnboksEdit(); }} title="Match bank-transaksjon"
             width={innboksEdit ? 760 : 620}
             footer={<button className="btn-ghost" onClick={() => { setAktiv(null); lukkInnboksEdit(); }}>Lukk</button>}>
        {aktiv && (
          <>
            <Card style={{ marginBottom: 12, padding: 10 }}>
              <div className="flex justify-between"><strong>{dato(aktiv.dato)}</strong>
                <span style={{ color: aktiv.belop >= 0 ? 'var(--theme-success)' : 'var(--theme-danger)' }}>{kr(aktiv.belop)}</span></div>
              <div className="fm-text-muted" style={{ fontSize: 12 }}>{aktiv.tekst}</div>
              {aktiv.kid && <div className="fm-text-muted" style={{ fontSize: 11 }}>KID: {aktiv.kid}</div>}
            </Card>
            <h4 style={{ margin: '0 0 8px', fontSize: 12 }}>
              <Search size={12} /> Forslag (sortert etter treffsannsynlighet)
            </h4>
            {forslag.length === 0 ? (
              <p className="fm-text-muted" style={{ fontSize: 12 }}>
                Ingen automatiske forslag. {aktiv.belop > 0
                  ? 'Match manuelt mot faktura via Faktura-siden.'
                  : 'Match manuelt mot leverandørfaktura, eller opprett nytt bilag under.'}
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Type</th><th>Ref</th><th>Mottaker</th>
                    <th style={{ textAlign: 'right' }}>Sum</th><th>Score</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {forslag.map((sug, idx) => {
                    if (sug.faktura) return (
                      <tr key={`f${idx}`}>
                        <td><span className="badge badge-info">faktura</span></td>
                        <td>{sug.faktura.fakturanummer}</td>
                        <td>{sug.faktura.kunde_navn}</td>
                        <td style={{ textAlign: 'right' }}>{kr(sug.faktura.sum_ink_mva)}</td>
                        <td><span className="badge badge-info">{sug.score}</span></td>
                        <td><button className="btn-primary" onClick={() => matchTil('faktura_id', sug.faktura.id)}>Match</button></td>
                      </tr>
                    );
                    if (sug.lev_faktura) return (
                      <tr key={`l${idx}`}>
                        <td><span className="badge badge-warning">lev.faktura</span></td>
                        <td>{sug.lev_faktura.fakturanummer || `#${sug.lev_faktura.id}`}</td>
                        <td>{sug.lev_faktura.leverandor_navn}</td>
                        <td style={{ textAlign: 'right' }}>{kr(sug.lev_faktura.sum_ink_mva)}</td>
                        <td><span className="badge badge-info">{sug.score}</span></td>
                        <td><button className="btn-primary" onClick={() => matchTil('lev_faktura_id', sug.lev_faktura.id)}>Match & bokfør</button></td>
                      </tr>
                    );
                    if (sug.innboks) {
                      const o = (() => { try { return sug.innboks.ocr_data ? JSON.parse(sug.innboks.ocr_data) : {}; } catch { return {}; } })();
                      return (
                        <tr key={`i${idx}`}>
                          <td><span className="badge badge-neutral">innboks</span></td>
                          <td>{sug.innboks.filnavn}</td>
                          <td>{o.leverandor || o.beskrivelse || '—'}</td>
                          <td style={{ textAlign: 'right' }}>{o.sum != null ? kr(o.sum) : '—'}</td>
                          <td><span className="badge badge-info">{sug.score}</span></td>
                          <td><button className="btn-primary" style={{ whiteSpace: 'nowrap' }} onClick={() => apneInnboksEdit(sug.innboks)}>Match & lag bilag</button></td>
                        </tr>
                      );
                    }
                    return null;
                  })}
                </tbody>
              </table>
            )}

            {innboksEdit && (
              <Card style={{ marginTop: 12, padding: 12 }}>
                <h4 style={{ margin: '0 0 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FilePlus size={13} /> Bilag fra innboks — juster før match
                </h4>
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 11 }}>Dato</label>
                    <input type="date" value={innboksEdit.dato}
                           onChange={(e) => setInnboksEdit({ ...innboksEdit, dato: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11 }}>Kategori</label>
                    <input value={innboksEdit.kategori}
                           onChange={(e) => setInnboksEdit({ ...innboksEdit, kategori: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 11 }}>Beskrivelse</label>
                    <input value={innboksEdit.beskrivelse}
                           onChange={(e) => setInnboksEdit({ ...innboksEdit, beskrivelse: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11 }}>Sum (ink. MVA)</label>
                    <input type="number" step="0.01" value={innboksEdit.sum}
                           onChange={(e) => setInnboksEdit({ ...innboksEdit, sum: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11 }}>MVA-beløp</label>
                    <input type="number" step="0.01" value={innboksEdit.mva}
                           onChange={(e) => setInnboksEdit({ ...innboksEdit, mva: e.target.value })} />
                  </div>
                </div>
                {innboksBildeUrl && (
                  <div style={{ marginTop: 10 }}>
                    {(innboksEdit.mime === 'application/pdf' || /\.pdf$/i.test(innboksEdit.filnavn)) ? (
                      <iframe src={innboksBildeUrl} title="forhåndsvisning"
                              style={{ width: '100%', height: 320, border: 'none', background: '#000', borderRadius: 6 }} />
                    ) : (
                      <img src={innboksBildeUrl} alt="kvittering"
                           style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 6, display: 'block' }} />
                    )}
                  </div>
                )}
                <div className="flex" style={{ gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                  <button className="btn-ghost" onClick={lukkInnboksEdit}>Avbryt</button>
                  <button className="btn-primary" onClick={bekreftInnboksMatch}>Lagre & match</button>
                </div>
              </Card>
            )}

            {/* Opprett nytt bilag direkte fra transaksjonen */}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--theme-border-light, #2222)' }}>
              {!bilagForm ? (
                <button className="btn-secondary" onClick={() => startNyttBilag(aktiv)}>
                  <FilePlus size={13} /> Opprett nytt bilag fra denne transaksjonen
                </button>
              ) : (
                <Card style={{ padding: 12 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FilePlus size={13} /> Nytt bilag — kr {kr(Math.abs(aktiv.belop))} ({bilagForm.type})
                  </h4>
                  <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 11 }}>Beskrivelse</label>
                      <input value={bilagForm.beskrivelse}
                             onChange={(e) => setBilagForm({ ...bilagForm, beskrivelse: e.target.value })} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11 }}>
                        Hovedkonto <span style={{ color: 'var(--theme-danger)' }}>*</span>
                      </label>
                      <input value={bilagForm.hovedkonto}
                             onChange={(e) => setBilagForm({ ...bilagForm, hovedkonto: e.target.value })}
                             placeholder={bilagForm.type === 'utgift' ? 'f.eks. 6995 (annen kostnad)' : 'f.eks. 3000 (salgsinntekt)'} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11 }}>Motkonto (balanse)</label>
                      <input value={bilagForm.motkonto}
                             onChange={(e) => setBilagForm({ ...bilagForm, motkonto: e.target.value })} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11 }}>MVA-sats</label>
                      <select value={bilagForm.mva_sats}
                              onChange={(e) => setBilagForm({ ...bilagForm, mva_sats: parseFloat(e.target.value) })}>
                        <option value={0}>0 % (fritatt)</option>
                        <option value={12}>12 %</option>
                        <option value={15}>15 %</option>
                        <option value={25}>25 %</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11 }}>Type</label>
                      <select value={bilagForm.type}
                              onChange={(e) => setBilagForm({ ...bilagForm, type: e.target.value })}>
                        <option value="utgift">utgift</option>
                        <option value="inntekt">inntekt</option>
                      </select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Paperclip size={11} /> Vedlegg (kvittering/faktura, valgfri)
                      </label>
                      <input ref={bilagFil} type="file"
                             accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn-primary" onClick={opprettOgMatch}>
                      <FilePlus size={13} /> Opprett og match
                    </button>
                    <button className="btn-ghost" onClick={() => setBilagForm(null)}>Avbryt</button>
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
      </Modal>

      <Modal open={importOpen} onClose={() => setImportOpen(false)}
             title="Importer bankutdrag (CSV)">
        <div className="grid" style={{ gap: 10 }}>
          <div>
            <label style={{ fontSize: 11 }}>Bankforbindelse / konto</label>
            {bfs.length === 0 ? (
              <p className="fm-text-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                Ingen bankforbindelser registrert. Opprett under «Bankforbindelser»-fanen først,
                ellers importeres linjene uten kobling.
              </p>
            ) : (
              <select value={bfImport} onChange={(e) => setBfImport(e.target.value)}
                      style={{ width: '100%' }}>
                <option value="">— uten kobling —</option>
                {bfs.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.navn || b.bank_navn || `#${b.id}`}
                    {b.kontonummer ? ` — ${b.kontonummer}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label style={{ fontSize: 11 }}>CSV-fil</label>
            <input ref={fil} type="file" accept=".csv,.txt"
                   onChange={(e) => setImportFil(e.target.files?.[0] || null)} />
            {importFil && (
              <div className="fm-text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                {importFil.name} ({Math.round(importFil.size / 1024)} kB)
              </div>
            )}
          </div>
          <div className="flex" style={{ gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
            <button className="btn-ghost" onClick={() => setImportOpen(false)} disabled={busy}>Avbryt</button>
            <button className="btn-primary" onClick={importer} disabled={busy || !importFil}>
              <Upload size={13} /> {busy ? 'Importerer…' : 'Importer'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
// ── Bankforbindelser-tab ────────────────────────────────────────────────────
const tomBF = () => ({
  navn: '', bank_navn: '', kontonummer: '', iban: '', bic: '',
  valuta: 'NOK', hovedbok_konto: '1920', provider: 'manuell',
  ekstern_konto_id: '',
});

const Forbindelser = () => {
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tomBF());
  const [gcOpen, setGcOpen] = useState(false);
  const [gcInst, setGcInst] = useState(null);
  const [gcSok, setGcSok] = useState('');
  const [gcKonfigurert, setGcKonfigurert] = useState(null);
  const [gcBusy, setGcBusy] = useState(false);
  const [ebOpen, setEbOpen] = useState(false);
  const [ebInst, setEbInst] = useState(null);
  const [ebSok, setEbSok] = useState('');
  const [ebKonfigurert, setEbKonfigurert] = useState(null);
  const [ebBusy, setEbBusy] = useState(false);
  const [ebPsuType, setEbPsuType] = useState('business');

  const last = () => api.get('/bankforbindelse').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);
  useEffect(() => {
    api.get('/bank/gocardless/status')
      .then((r) => setGcKonfigurert(!!r.data?.konfigurert))
      .catch(() => setGcKonfigurert(false));
  }, []);
  useEffect(() => {
    api.get('/bank/enablebanking/status')
      .then((r) => setEbKonfigurert(!!r.data?.konfigurert))
      .catch(() => setEbKonfigurert(false));
  }, []);

  // Ferdigstill GoCardless-flyt etter redirect tilbake fra bank (?ref=...)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const ref = sp.get('ref');
    const req = sessionStorage.getItem('gc_pending_req');
    if (ref && req) {
      sessionStorage.removeItem('gc_pending_req');
      api.post('/bank/gocardless/fullfor', { requisition_id: req })
        .then((r) => {
          alert(`Koblet til ${r.data.antall} konto(er).`);
          last();
        })
        .catch((e) => alert(e.response?.data?.error || 'Fullføring feilet.'))
        .finally(() => {
          sp.delete('ref');
          const ny = window.location.pathname + (sp.toString() ? `?${sp}` : '');
          window.history.replaceState({}, '', ny);
        });
    }
  }, []);

  const aapneGc = async () => {
    if (!gcKonfigurert) {
      alert('GoCardless er ikke konfigurert på server. Sett GOCARDLESS_SECRET_ID og GOCARDLESS_SECRET_KEY.');
      return;
    }
    setGcOpen(true);
    if (!gcInst) {
      try {
        const r = await api.get('/bank/gocardless/institusjoner', { params: { country: 'no' } });
        setGcInst(r.data || []);
      } catch (e) {
        alert(e.response?.data?.error || 'Kunne ikke laste banker.');
      }
    }
  };

  const gcKoble = async (inst) => {
    setGcBusy(true);
    try {
      const redirect = `${window.location.origin}/bank`;
      const r = await api.post('/bank/gocardless/koble', {
        institusjon_id: inst.id, redirect,
      });
      sessionStorage.setItem('gc_pending_req', r.data.requisition_id);
      window.location.href = r.data.link;
    } catch (e) {
      alert(e.response?.data?.error || 'Kobling feilet.');
      setGcBusy(false);
    }
  };

  // Ferdigstill Enable Banking-flyt etter redirect tilbake (?code=...)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const code = sp.get('code');
    const venter = sessionStorage.getItem('eb_pending');
    if (code && venter) {
      sessionStorage.removeItem('eb_pending');
      api.post('/bank/enablebanking/fullfor', { code })
        .then((r) => { alert(`Koblet til ${r.data.antall} konto(er).`); last(); })
        .catch((e) => alert(e.response?.data?.error || 'Fullføring feilet.'))
        .finally(() => {
          ['code', 'state'].forEach((k) => sp.delete(k));
          const ny = window.location.pathname + (sp.toString() ? `?${sp}` : '');
          window.history.replaceState({}, '', ny);
        });
    }
  }, []);

  const aapneEb = async () => {
    if (!ebKonfigurert) {
      alert('Enable Banking er ikke konfigurert på server. Sett ENABLE_BANKING_APP_ID og ENABLE_BANKING_PRIVATE_KEY.');
      return;
    }
    setEbOpen(true);
    if (!ebInst) {
      try {
        const r = await api.get('/bank/enablebanking/institusjoner', { params: { country: 'no', psu_type: ebPsuType } });
        setEbInst(r.data || []);
      } catch (e) {
        alert(e.response?.data?.error || 'Kunne ikke laste banker.');
      }
    }
  };

  const ebByttPsuType = async (t) => {
    setEbPsuType(t);
    setEbInst(null);
    try {
      const r = await api.get('/bank/enablebanking/institusjoner', { params: { country: 'no', psu_type: t } });
      setEbInst(r.data || []);
    } catch (e) {
      alert(e.response?.data?.error || 'Kunne ikke laste banker.');
    }
  };

  const ebKoble = async (inst) => {
    setEbBusy(true);
    try {
      const redirect = `${window.location.origin}/bank`;
      const r = await api.post('/bank/enablebanking/koble', {
        institusjon_id: inst.id, land: 'no', psu_type: ebPsuType, redirect,
      });
      sessionStorage.setItem('eb_pending', '1');
      window.location.href = r.data.link;
    } catch (e) {
      alert(e.response?.data?.error || 'Kobling feilet.');
      setEbBusy(false);
    }
  };

  const aapneNy = () => { setRedigerer(null); setSkjema(tomBF()); setOpen(true); };
  const aapneRed = (b) => {
    setRedigerer(b);
    setSkjema({
      navn: b.navn, bank_navn: b.bank_navn || '', kontonummer: b.kontonummer || '',
      iban: b.iban || '', bic: b.bic || '', valuta: b.valuta || 'NOK',
      hovedbok_konto: b.hovedbok_konto || '1920',
      provider: b.provider || 'manuell',
      ekstern_konto_id: b.ekstern_konto_id || '',
    });
    setOpen(true);
  };

  const lagre = async () => {
    if (!skjema.navn) { alert('Navn er påkrevd.'); return; }
    try {
      if (redigerer) await api.put(`/bankforbindelse/${redigerer.id}`, skjema);
      else await api.post('/bankforbindelse', skjema);
      setOpen(false); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const slett = async (b) => {
    if (!window.confirm(`Slette bankforbindelsen «${b.navn}»? Transaksjoner beholdes.`)) return;
    try { await api.delete(`/bankforbindelse/${b.id}`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const koble = async (b) => {
    try {
      const r = await api.post(`/bankforbindelse/${b.id}/koble`, {});
      if (r.data.auth_url) {
        window.open(r.data.auth_url, '_blank', 'noopener');
      } else {
        alert('Ingen auth-URL returnert.');
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Kobling feilet.');
    }
  };

  const sync = async (b) => {
    try {
      const r = await api.post(`/bankforbindelse/${b.id}/sync`, {});
      alert(r.data.info || `${r.data.nye} nye transaksjoner.`);
      last();
    } catch (e) { alert(e.response?.data?.error || 'Sync feilet.'); }
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="fm-text-muted" style={{ fontSize: 12 }}>
            Koble til banken via PSD2 for automatisk import av transaksjoner,
            eller bruk «manuell» for CSV-import.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" onClick={aapneEb}
                    title={ebKonfigurert === false ? 'Enable Banking ikke konfigurert' : 'Koble til via PSD2 (DNB, Sparebank 1, Nordea m.fl.)'}
                    disabled={ebKonfigurert === false}>
              <Wifi size={14} /> Koble til bank (PSD2)
            </button>
            {gcKonfigurert && (
              <button className="btn-ghost" onClick={aapneGc}
                      title="Koble til via GoCardless (PSD2)">
                <Wifi size={14} /> GoCardless
              </button>
            )}
            <button className="btn-primary" onClick={aapneNy}>
              <Plus size={14} /> Ny forbindelse
            </button>
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        {liste.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Building2 size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
            <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>
              Ingen bankforbindelser registrert.
            </p>
          </div>
        ) : (
          <table>
            <thead><tr>
              <th>Navn</th><th>Bank</th><th>Kontonummer</th><th>Provider</th>
              <th>Status</th><th>Sist sync</th><th>Hovedbok</th><th></th>
            </tr></thead>
            <tbody>
              {liste.map((b) => (
                <tr key={b.id}>
                  <td><b>{b.navn}</b></td>
                  <td>{b.bank_navn || '—'}</td>
                  <td>{b.kontonummer || '—'}</td>
                  <td><span className="badge badge-neutral">{b.provider}</span></td>
                  <td>
                    {b.tilkoblet
                      ? <span style={{ color: 'var(--theme-success)' }}><Wifi size={12} /> tilkoblet</span>
                      : <span className="fm-text-muted"><WifiOff size={12} /> ikke koblet</span>}
                  </td>
                  <td className="fm-text-muted" style={{ fontSize: 11 }}>
                    {b.sist_sync ? dato(b.sist_sync.slice(0, 10)) : '—'}
                  </td>
                  <td>{b.hovedbok_konto}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {b.provider !== 'manuell' && !b.tilkoblet && (
                      <button className="btn-ghost" title="Koble til" onClick={() => koble(b)}>
                        <Wifi size={14} />
                      </button>
                    )}
                    {b.provider !== 'manuell' && b.tilkoblet && (
                      <button className="btn-ghost" title="Synk nå" onClick={() => sync(b)}>
                        <RefreshCw size={14} />
                      </button>
                    )}
                    <button className="btn-ghost" onClick={() => aapneRed(b)}><Pencil size={14} /></button>
                    <button className="btn-ghost" onClick={() => slett(b)}>
                      <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger bankforbindelse' : 'Ny bankforbindelse'}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Navn (internt) *</label>
            <input value={skjema.navn} onChange={(e) => setSkjema({ ...skjema, navn: e.target.value })} />
          </div>
          <div>
            <label>Bank</label>
            <input value={skjema.bank_navn} onChange={(e) => setSkjema({ ...skjema, bank_navn: e.target.value })} placeholder="DNB, SpareBank 1, …" />
          </div>
          <div>
            <label>Kontonummer</label>
            <input value={skjema.kontonummer} onChange={(e) => setSkjema({ ...skjema, kontonummer: e.target.value })} />
          </div>
          <div>
            <label>IBAN</label>
            <input value={skjema.iban} onChange={(e) => setSkjema({ ...skjema, iban: e.target.value })} />
          </div>
          <div>
            <label>BIC</label>
            <input value={skjema.bic} onChange={(e) => setSkjema({ ...skjema, bic: e.target.value })} />
          </div>
          <div>
            <label>Valuta</label>
            <input value={skjema.valuta} onChange={(e) => setSkjema({ ...skjema, valuta: e.target.value })} />
          </div>
          <div>
            <label>Hovedbokskonto</label>
            <input value={skjema.hovedbok_konto} onChange={(e) => setSkjema({ ...skjema, hovedbok_konto: e.target.value })} />
          </div>
          <div>
            <label>Provider</label>
            <select value={skjema.provider} onChange={(e) => setSkjema({ ...skjema, provider: e.target.value })}>
              <option value="manuell">Manuell (CSV)</option>
              <option value="aiia">Aiia (PSD2)</option>
              <option value="tink">Tink (PSD2)</option>
              <option value="nordic-api">Nordic API Gateway</option>
            </select>
          </div>
          {skjema.provider !== 'manuell' && (
            <div>
              <label>Ekstern konto-ID</label>
              <input value={skjema.ekstern_konto_id}
                     onChange={(e) => setSkjema({ ...skjema, ekstern_konto_id: e.target.value })}
                     placeholder="ID hos provider" />
            </div>
          )}
        </div>
        {skjema.provider !== 'manuell' && (
          <div style={{ marginTop: 12, padding: 8, background: 'var(--theme-bg-input)', borderRadius: 4, fontSize: 11 }}>
            <b>Merk:</b> PSD2-tilkobling krever at <code>{skjema.provider.toUpperCase()}_CLIENT_ID</code> og{' '}
            <code>{skjema.provider.toUpperCase()}_CLIENT_SECRET</code> er satt som env-variabler hos hosting.
            CSV-import virker uavhengig av dette.
          </div>
        )}
      </Modal>

      <Modal open={gcOpen} onClose={() => setGcOpen(false)}
             title="Koble til bank via PSD2 (GoCardless)"
             footer={<button className="btn-ghost" onClick={() => setGcOpen(false)}>Lukk</button>}>
        <div className="fm-text-muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Velg banken din. Du blir sendt til bankens innloggingsside for å godkjenne
          lese-tilgang til transaksjoner (gyldig i 90 dager).
        </div>
        <input value={gcSok} onChange={(e) => setGcSok(e.target.value)}
               placeholder="Søk bank…" style={{ marginBottom: 10, width: '100%' }} />
        {gcInst == null ? (
          <span className="fm-text-muted">Laster banker…</span>
        ) : (
          <div style={{ maxHeight: 360, overflowY: 'auto', display: 'grid', gap: 4 }}>
            {gcInst
              .filter((i) => !gcSok || (i.navn || '').toLowerCase().includes(gcSok.toLowerCase()))
              .map((i) => (
                <button key={i.id} disabled={gcBusy}
                        onClick={() => gcKoble(i)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10,
                                 padding: '8px 10px', textAlign: 'left',
                                 background: 'var(--theme-bg-card)',
                                 border: '1px solid var(--theme-border)',
                                 borderRadius: 4, cursor: 'pointer' }}>
                  {i.logo && <img src={i.logo} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />}
                  <span style={{ flex: 1 }}>{i.navn}</span>
                  <span className="fm-text-muted" style={{ fontSize: 11 }}>{i.bic}</span>
                </button>
              ))}
          </div>
        )}
      </Modal>

      <Modal open={ebOpen} onClose={() => setEbOpen(false)}
             title="Koble til bank via PSD2"
             footer={<button className="btn-ghost" onClick={() => setEbOpen(false)}>Lukk</button>}>
        <div className="fm-text-muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Velg banken din. Du blir sendt til bankens innloggingsside for å godkjenne
          lese-tilgang til transaksjoner (gyldig i inntil 90 dager).
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <button className={ebPsuType === 'business' ? 'btn-primary' : 'btn-ghost'}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={() => ebByttPsuType('business')}>Bedrift</button>
          <button className={ebPsuType === 'personal' ? 'btn-primary' : 'btn-ghost'}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={() => ebByttPsuType('personal')}>Privat</button>
        </div>
        <input value={ebSok} onChange={(e) => setEbSok(e.target.value)}
               placeholder="Søk bank…" style={{ marginBottom: 10, width: '100%' }} />
        {ebInst == null ? (
          <span className="fm-text-muted">Laster banker…</span>
        ) : (
          <div style={{ maxHeight: 360, overflowY: 'auto', display: 'grid', gap: 4 }}>
            {ebInst
              .filter((i) => !ebSok || (i.navn || '').toLowerCase().includes(ebSok.toLowerCase()))
              .map((i) => (
                <button key={i.id} disabled={ebBusy}
                        onClick={() => ebKoble(i)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10,
                                 padding: '8px 10px', textAlign: 'left',
                                 background: 'var(--theme-bg-card)',
                                 border: '1px solid var(--theme-border)',
                                 borderRadius: 4, cursor: 'pointer' }}>
                  {i.logo && <img src={i.logo} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />}
                  <span style={{ flex: 1 }}>{i.navn}</span>
                  <span className="fm-text-muted" style={{ fontSize: 11 }}>{i.bic}</span>
                </button>
              ))}
          </div>
        )}
      </Modal>
    </>
  );
};
const Avstemming = () => {
  const [fra, setFra] = useState(forsteIaar());
  const [til, setTil] = useState(idag());
  const [konto, setKonto] = useState('1920');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const kjor = async () => {
    setBusy(true);
    try {
      const r = await api.get('/bank/avstemming', { params: { fra, til, konto } });
      setData(r.data);
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
    setBusy(false);
  };

  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <div>
            <label>Fra dato</label>
            <input type="date" value={fra} onChange={(e) => setFra(e.target.value)} />
          </div>
          <div>
            <label>Til dato</label>
            <input type="date" value={til} onChange={(e) => setTil(e.target.value)} />
          </div>
          <div>
            <label>Hovedbokskonto</label>
            <input value={konto} onChange={(e) => setKonto(e.target.value)} />
          </div>
          <button className="btn-primary" onClick={kjor} disabled={busy}>
            <Scale size={14} /> {busy ? 'Beregner…' : 'Kjør avstemming'}
          </button>
        </div>
      </Card>

      {data && (
        <>
          <Card style={{ marginBottom: 12, padding: 16, textAlign: 'center',
                         background: data.i_balanse ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)' }}>
            <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Avvik (bank − hovedbok)</div>
            <div style={{ fontSize: 28, fontWeight: 700,
                          color: data.i_balanse ? 'var(--theme-success)' : 'var(--theme-danger)' }}>
              {kr(data.diff)}
            </div>
            <div className="fm-text-muted" style={{ fontSize: 12 }}>
              {data.i_balanse ? 'Bank og hovedbok er i balanse.'
                              : 'Det er avvik. Sjekk umatchede transaksjoner og periode.'}
            </div>
          </Card>

          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Card style={{ padding: 14 }}>
              <h3 style={{ marginTop: 0, fontSize: 14 }}>Bank ({data.bank.antall_total} transaksjoner)</h3>
              <table>
                <tbody>
                  <tr><td>Innbetalt</td><td style={{ textAlign: 'right', color: 'var(--theme-success)' }}>{kr(data.bank.inn)}</td></tr>
                  <tr><td>Utbetalt</td><td style={{ textAlign: 'right', color: 'var(--theme-danger)' }}>−{kr(data.bank.ut)}</td></tr>
                  <tr><td><b>Netto bevegelse</b></td><td style={{ textAlign: 'right' }}><b>{kr(data.bank.netto)}</b></td></tr>
                  <tr><td className="fm-text-muted">Matchet</td><td style={{ textAlign: 'right' }} className="fm-text-muted">{kr(data.bank.matchet)}</td></tr>
                  <tr><td className="fm-text-muted">Umatchet ({data.bank.antall_ny})</td><td style={{ textAlign: 'right' }} className="fm-text-muted">{kr(data.bank.umatchet)}</td></tr>
                </tbody>
              </table>
            </Card>
            <Card style={{ padding: 14 }}>
              <h3 style={{ marginTop: 0, fontSize: 14 }}>Hovedbok konto {data.hovedbok.konto}</h3>
              <table>
                <tbody>
                  <tr><td>Debet (inn)</td><td style={{ textAlign: 'right' }}>{kr(data.hovedbok.debet)}</td></tr>
                  <tr><td>Kredit (ut)</td><td style={{ textAlign: 'right' }}>{kr(data.hovedbok.kredit)}</td></tr>
                  <tr><td><b>Netto bevegelse</b></td><td style={{ textAlign: 'right' }}><b>{kr(data.hovedbok.netto)}</b></td></tr>
                </tbody>
              </table>
            </Card>
          </div>
        </>
      )}
    </>
  );
};

// ── Regelmotor-tab ──────────────────────────────────────────────────────────
const tomR = () => ({
  navn: '', monster: '', retning: 'begge',
  belop_min: '', belop_maks: '',
  handling: 'bokfor', konto: '', motkonto: '1920',
  kategori: '', mva_sats: 0, prioritet: 100, aktiv: true,
});

const Regler = () => {
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tomR());
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);

  const last = () => api.get('/bank/regler').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);

  const aapneNy = () => { setRedigerer(null); setSkjema(tomR()); setTest(null); setOpen(true); };
  const aapneRed = (r) => {
    setRedigerer(r);
    setSkjema({
      navn: r.navn, monster: r.monster, retning: r.retning,
      belop_min: r.belop_min ?? '', belop_maks: r.belop_maks ?? '',
      handling: r.handling, konto: r.konto || '', motkonto: r.motkonto || '1920',
      kategori: r.kategori || '', mva_sats: r.mva_sats || 0,
      prioritet: r.prioritet || 100, aktiv: !!r.aktiv,
    });
    setTest(null);
    setOpen(true);
  };

  const lagre = async () => {
    if (!skjema.navn || !skjema.monster) { alert('Navn og mønster må fylles inn.'); return; }
    const body = {
      ...skjema,
      belop_min: skjema.belop_min === '' ? null : parseFloat(skjema.belop_min),
      belop_maks: skjema.belop_maks === '' ? null : parseFloat(skjema.belop_maks),
      mva_sats: parseFloat(skjema.mva_sats || 0),
      prioritet: parseInt(skjema.prioritet || 100),
    };
    try {
      if (redigerer) await api.put(`/bank/regler/${redigerer.id}`, body);
      else await api.post('/bank/regler', body);
      setOpen(false); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const slett = async (r) => {
    if (!window.confirm(`Slette regel "${r.navn}"?`)) return;
    try { await api.delete(`/bank/regler/${r.id}`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const testRegel = async () => {
    if (!redigerer) { alert('Lagre regelen først for å teste.'); return; }
    try {
      const r = await api.get(`/bank/regler/${redigerer.id}/test`);
      setTest(r.data);
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const anvendAlle = async () => {
    if (!window.confirm('Anvend alle aktive regler på alle "ny"-transaksjoner?')) return;
    setBusy(true);
    try {
      const r = await api.post('/bank/regler/anvend');
      alert(`Behandlet: ${r.data.behandlet} (${r.data.bokfort} bokført, ${r.data.ignorert} ignorert).`);
      last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
    setBusy(false);
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex justify-between items-center" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span className="fm-text-muted" style={{ fontSize: 12 }}>
            {liste.length} regler — anvendes i prioritetsrekkefølge på nye banktransaksjoner.
          </span>
          <div className="flex" style={{ gap: 6 }}>
            <button className="btn-ghost" onClick={anvendAlle} disabled={busy || liste.length === 0}>
              <Zap size={14} /> {busy ? 'Anvender…' : 'Anvend alle nå'}
            </button>
            <button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Ny regel</button>
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        {liste.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Scale size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
            <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>
              Ingen regler ennå. Lag regler for faste kostnader (bankgebyr, husleie, abonnementer, lønn)
              for å automatisere bokføringen.
            </p>
          </div>
        ) : (
          <table>
            <thead><tr>
              <th>Pri</th><th>Navn</th><th>Mønster</th><th>Retning</th>
              <th>Beløp</th><th>Handling</th><th>Konto</th>
              <th style={{ textAlign: 'right' }}>Treff</th><th>Aktiv</th><th></th>
            </tr></thead>
            <tbody>
              {liste.map((r) => (
                <tr key={r.id} style={{ opacity: r.aktiv ? 1 : 0.5 }}>
                  <td>{r.prioritet}</td>
                  <td><b>{r.navn}</b></td>
                  <td className="fm-text-muted" style={{ fontSize: 12 }}>
                    <code>{r.monster}</code>
                  </td>
                  <td>{r.retning}</td>
                  <td className="fm-text-muted" style={{ fontSize: 11 }}>
                    {r.belop_min != null || r.belop_maks != null
                      ? `${r.belop_min ?? '0'} – ${r.belop_maks ?? '∞'}`
                      : '—'}
                  </td>
                  <td>
                    {r.handling === 'bokfor'
                      ? <span className="badge badge-success">bokfør</span>
                      : <span className="badge badge-neutral">ignorer</span>}
                  </td>
                  <td>{r.handling === 'bokfor' ? `${r.konto || '?'} / ${r.motkonto}` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{r.antall_treff}</td>
                  <td>{r.aktiv
                    ? <span className="badge badge-success">aktiv</span>
                    : <span className="badge badge-neutral">av</span>}</td>
                  <td>
                    <button className="btn-ghost" onClick={() => aapneRed(r)}><Pencil size={14} /></button>
                    <button className="btn-ghost" onClick={() => slett(r)}>
                      <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger regel' : 'Ny regel'}
             width={680}
             footer={<>
               {redigerer && <button className="btn-ghost" onClick={testRegel}>
                 <Search size={14} /> Test mot nye transaksjoner
               </button>}
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}><label>Navn *</label>
            <input value={skjema.navn} onChange={(e) => setSkjema({ ...skjema, navn: e.target.value })}
                   placeholder="f.eks. DNB månedsgebyr" /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Mønster (fritekst, case-insensitive) *</label>
            <input value={skjema.monster} onChange={(e) => setSkjema({ ...skjema, monster: e.target.value })}
                   placeholder="f.eks. 'månedsgebyr' eller 'Spotify'" /></div>
          <div><label>Retning</label>
            <select value={skjema.retning} onChange={(e) => setSkjema({ ...skjema, retning: e.target.value })}>
              <option value="begge">Begge</option>
              <option value="inn">Innbetaling</option>
              <option value="ut">Utbetaling</option>
            </select></div>
          <div><label>Prioritet (lavere = først)</label>
            <input type="number" value={skjema.prioritet}
                   onChange={(e) => setSkjema({ ...skjema, prioritet: e.target.value })} /></div>
          <div><label>Beløp min (abs)</label>
            <input type="number" step="0.01" value={skjema.belop_min}
                   onChange={(e) => setSkjema({ ...skjema, belop_min: e.target.value })} /></div>
          <div><label>Beløp maks (abs)</label>
            <input type="number" step="0.01" value={skjema.belop_maks}
                   onChange={(e) => setSkjema({ ...skjema, belop_maks: e.target.value })} /></div>
          <div><label>Handling</label>
            <select value={skjema.handling} onChange={(e) => setSkjema({ ...skjema, handling: e.target.value })}>
              <option value="bokfor">Bokfør automatisk</option>
              <option value="ignorer">Ignorer</option>
            </select></div>
          <div><label>Aktiv</label>
            <select value={skjema.aktiv ? '1' : '0'}
                    onChange={(e) => setSkjema({ ...skjema, aktiv: e.target.value === '1' })}>
              <option value="1">Aktiv</option>
              <option value="0">Deaktivert</option>
            </select></div>
          {skjema.handling === 'bokfor' && (
            <>
              <div><label>Hovedkonto (kostnad/inntekt)</label>
                <input value={skjema.konto} onChange={(e) => setSkjema({ ...skjema, konto: e.target.value })}
                       placeholder="f.eks. 7790" /></div>
              <div><label>Motkonto (bank)</label>
                <input value={skjema.motkonto}
                       onChange={(e) => setSkjema({ ...skjema, motkonto: e.target.value })} /></div>
              <div><label>Kategori (Bilag)</label>
                <input value={skjema.kategori}
                       onChange={(e) => setSkjema({ ...skjema, kategori: e.target.value })}
                       placeholder="f.eks. Bankgebyr" /></div>
              <div><label>MVA-sats (%)</label>
                <select value={skjema.mva_sats}
                        onChange={(e) => setSkjema({ ...skjema, mva_sats: parseFloat(e.target.value) })}>
                  <option value={0}>0 %</option>
                  <option value={12}>12 %</option>
                  <option value={15}>15 %</option>
                  <option value={25}>25 %</option>
                </select></div>
            </>
          )}
        </div>

        {test && (
          <Card style={{ marginTop: 12, padding: 8 }}>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              <b>{test.antall}</b> nye transaksjoner matcher dette mønsteret:
            </div>
            {test.treff.length === 0 ? (
              <div className="fm-text-muted" style={{ fontSize: 12 }}>Ingen treff.</div>
            ) : (
              <table style={{ fontSize: 12 }}>
                <thead><tr><th>Dato</th><th>Tekst</th><th style={{ textAlign: 'right' }}>Beløp</th></tr></thead>
                <tbody>
                  {test.treff.map((t) => (
                    <tr key={t.id}>
                      <td>{dato(t.dato)}</td>
                      <td>{t.tekst}</td>
                      <td style={{ textAlign: 'right' }}>{kr(t.belop)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}
      </Modal>
    </>
  );
};

export default Bank;
